import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArtistPayee, PAYEE_DEFAULT_SHARE_RATE, payeeGet, payeeSet } from "@/services/firebase/artistHub";
import { PayeeDraft, percentToRate, rateToPercent, startOfDayMs, toDateInput, validatePayee } from "@/lib/fanPass";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useArtistConfirm } from "./ArtistConfirm";

const firstOfThisMonth = () => {
  const d = new Date();
  return toDateInput(new Date(d.getFullYear(), d.getMonth(), 1).getTime());
};
const toDraft = (p: ArtistPayee | null): PayeeDraft => ({
  payeeType: p?.payeeType ?? "person",
  name: p?.name ?? "",
  taxId: p?.taxId ?? "",
  sharePercent: rateToPercent(p?.shareRate ?? PAYEE_DEFAULT_SHARE_RATE),
  effectiveFrom: toDateInput(p?.effectiveFrom ?? null) || firstOfThisMonth(),
});

/**
 * B-23: 收款資料 (`artistPayees/{artistId}`, contract §1b payeeGet/payeeSet, staff only).
 * shareRate is the creator's cut of the distributable 70% (default 70% → creator = gross × 49%).
 * Tax ID is personal data: only staff can read it, it is never shown to the artist workspace.
 */
export default function ArtistPayeePanel({ artistId }: { artistId: string }) {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const key = ["artist-payee", artistId];
  const payee = useQuery({ queryKey: key, queryFn: () => payeeGet(artistId), retry: false });
  const [draft, setDraft] = useState<PayeeDraft | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (payee.isSuccess && draft === null) setDraft(toDraft(payee.data));
  }, [payee.isSuccess, payee.data, draft]);

  const save = useMutation({
    mutationFn: (d: PayeeDraft) =>
      payeeSet(artistId, {
        payeeType: d.payeeType,
        name: d.name.trim(),
        ...(d.taxId.trim() ? { taxId: d.taxId.trim().toUpperCase() } : {}),
        shareRate: percentToRate(d.sharePercent) ?? PAYEE_DEFAULT_SHARE_RATE,
        effectiveFrom: startOfDayMs(d.effectiveFrom) ?? Date.now(),
      }),
    onSuccess: (saved) => {
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
      if (saved) setDraft(toDraft(saved));
      setMessage("已儲存收款資料");
    },
  });

  const update = (patch: Partial<PayeeDraft>) => {
    setMessage("");
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };
  const onSave = () => {
    if (!draft) return;
    const found = validatePayee(draft);
    setErrors(found);
    if (found.length) return;
    const rate = percentToRate(draft.sharePercent) ?? PAYEE_DEFAULT_SHARE_RATE;
    const summary = [
      `收款對象：${draft.payeeType === "company" ? "公司" : "個人"}／${draft.name.trim()}`,
      `統編／身分證：${draft.taxId.trim() || "（未填）"}`,
      `創作者分潤：可分配的 ${rateToPercent(rate)}%（＝毛收入 ${rateToPercent(rate * 0.7)}%）`,
      `生效日：${draft.effectiveFrom}`,
    ];
    confirm({ title: "儲存收款資料", message: "確定儲存以下收款資料？", lines: summary, confirmText: "儲存", onConfirm: () => save.mutate(draft) });
  };

  const busy = save.isPending;
  const rate = draft ? percentToRate(draft.sharePercent) : null;
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>收款資料（鐵粉月卡分潤）</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          月報與季結算依此付款。分潤＝毛收入 × 70%（扣平台費 30%）× 創作者比例；預設比例 70%。付給個人需扣繳與二代健保，付給公司收發票（由財務確認）。
        </p>
        {payee.isPending && <p>讀取收款資料…</p>}
        {payee.isError && (
          <p role="alert" className="text-destructive">
            讀取失敗：{payee.error instanceof Error ? payee.error.message : "請重試"}
          </p>
        )}
        {payee.isSuccess && !payee.data && <p className="text-sm text-amber-700">尚未設定收款資料；報表會以預設 70% 計算並標示「未設定收款」。</p>}
        {draft && (
          <>
            <fieldset className="flex gap-4" disabled={busy}>
              <legend className="mb-1 text-sm font-medium">收款對象</legend>
              {(["person", "company"] as const).map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm">
                  <input type="radio" name={`payee-type-${artistId}`} checked={draft.payeeType === t} onChange={() => update({ payeeType: t })} />
                  {t === "person" ? "個人（藝人本人）" : "公司（經紀公司）"}
                </label>
              ))}
            </fieldset>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label htmlFor="payee-name">{draft.payeeType === "company" ? "公司名稱" : "姓名（與帳戶一致）"}</Label>
                <Input id="payee-name" value={draft.name} maxLength={100} disabled={busy} onChange={(e) => update({ name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="payee-tax">{draft.payeeType === "company" ? "統一編號（8 碼，選填）" : "身分證／居留證號（選填）"}</Label>
                <Input id="payee-tax" value={draft.taxId} maxLength={10} autoComplete="off" disabled={busy} onChange={(e) => update({ taxId: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="payee-share">創作者分潤比例（可分配收入的 %）</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="payee-share"
                    inputMode="decimal"
                    value={draft.sharePercent}
                    disabled={busy}
                    className="max-w-[120px]"
                    onChange={(e) => update({ sharePercent: e.target.value })}
                  />
                  <span>%</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {rate === null ? "請輸入 0–100" : `＝毛收入的 ${rateToPercent(rate * 0.7)}%（預設 70% → 49%）`}
                </p>
              </div>
              <div>
                <Label htmlFor="payee-from">生效日</Label>
                <Input id="payee-from" type="date" value={draft.effectiveFrom} disabled={busy} onChange={(e) => update({ effectiveFrom: e.target.value })} />
              </div>
            </div>
            {errors.length > 0 && (
              <ul role="alert" className="list-disc pl-5 text-sm text-destructive">
                {errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {save.error && (
              <p role="alert" className="text-destructive">
                儲存失敗：{save.error instanceof Error ? save.error.message : "請重試"}
              </p>
            )}
            {message && <p role="status">{message}</p>}
            <Button disabled={busy} onClick={onSave}>
              {busy ? "儲存中…" : "儲存收款資料"}
            </Button>
          </>
        )}
      </CardContent>
      {dialog}
    </Card>
  );
}
